// Event Director — phase + rotation calculator.
//
// All inputs are ISO date strings. Output is a "scene descriptor" that the
// Director page renders. Pure functions, easy to unit test.

export const PHASES = {
  PRE: "pre",
  ARRIVAL: "arrival",
  GROWING: "growing",
  BUILDUP: "buildup",
  COUNTDOWN: "countdown",
  REVEAL: "reveal",
  CAKE: "cake",
  DINNER: "dinner",
  FAREWELL: "farewell",
  POST: "post",
};

// Rotations (id, label, duration in seconds)
const ROT_ARRIVAL = [
  { id: "welcome", label: "Welcome",     secs: 40 },
  { id: "dance",   label: "Dance Floor", secs: 60 },
  { id: "join",    label: "Join Live",   secs: 20 },
  { id: "gallery", label: "Stars",       secs: 30 },
];

const ROT_GROWING = [
  { id: "dance",     label: "Dance Floor",      secs: 45 },
  { id: "gallery",   label: "Stars",            secs: 30 },
  { id: "memories",  label: "Memories",         secs: 60 },
  { id: "projector", label: "Anagha's Message", secs: 30 },
  { id: "stats",     label: "Blessings",        secs: 20 },
];

const ROT_DINNER = [
  { id: "memories",  label: "Memories",         secs: 50 },
  { id: "dance",     label: "Guest Photos",     secs: 40 },
  { id: "projector", label: "Anagha's Message", secs: 35 },
  { id: "gallery",   label: "Stars",            secs: 35 },
];

// Grand Reveal cinematic scenes (id, secs)
// Removed "Our Beloved Guests" (per user request). Renamed "Messages" → single
// "Message from Anagha" card instead of a wall of blessings.
export const REVEAL_SCENES = [
  { id: "title",     secs: 20 },
  { id: "stars",     secs: 30 },
  { id: "memories",  secs: 60 },
  { id: "messages",  secs: 60 },
  { id: "final",     secs: 30 },
];

const TOTAL_REVEAL_SECS = REVEAL_SCENES.reduce((a, b) => a + b.secs, 0);

// Buildup messages (cycled every ~20s)
export const BUILDUP_MESSAGES = [
  { te: "ఏదో అద్భుతం జరగబోతోంది...", en: "Something Magical Is About To Happen..." },
  { te: "యువరాణి ప్రత్యేక ధన్యవాదాలు సిద్ధం చేస్తోంది...", en: "The Princess Is Preparing A Special Thank You..." },
  { te: "రాజవేడుక త్వరలోనే మొదలవుతుంది...", en: "A Royal Celebration Begins Soon..." },
];

// Parse helper
function ms(iso) { return new Date(iso).getTime(); }

/**
 * Given the four anchor times, derive the auxiliary times the director needs.
 */
function deriveAnchors(cfg) {
  const start  = ms(cfg.start);
  const cake   = ms(cfg.cake);
  const dinner = ms(cfg.dinner);
  const end    = ms(cfg.end);
  // Build phase boundaries proportionally so the spec still works even if
  // host picks non-standard slots (the user explicitly wanted configurable).
  // Phase 1 = first 30 min of (start→cake). Phase 2 = next 40 min. Phase 3 = next 15 min.
  // Phase 4 (countdown) = last 5 min before cake. Reveal happens at cake.
  // For the default 90 minute window (7:30 → 9:00) these add up to 90.
  const window = cake - start;
  const arrivalEnd  = start + Math.round(window * (30 / 90));
  const growingEnd  = start + Math.round(window * (70 / 90));
  const buildupEnd  = start + Math.round(window * (85 / 90));
  // countdown runs from buildupEnd → cake
  return { start, cake, dinner, end, arrivalEnd, growingEnd, buildupEnd };
}

/**
 * Compute the current Director scene descriptor.
 *  cfg = { start, cake, dinner, end }
 *  nowMs = effective "now" (already fast-forwarded by caller if needed)
 */
export function computeScene(cfg, nowMs) {
  if (!cfg) return { phase: PHASES.PRE, scene: null };
  const a = deriveAnchors(cfg);

  if (nowMs < a.start) {
    return { phase: PHASES.PRE, scene: { id: "pre" }, anchors: a };
  }
  if (nowMs >= a.end) {
    return { phase: PHASES.POST, scene: { id: "post" }, anchors: a };
  }

  // Phase 1: Arrival rotation
  if (nowMs < a.arrivalEnd) {
    const slot = pickFromRotation(ROT_ARRIVAL, nowMs - a.start);
    return { phase: PHASES.ARRIVAL, scene: slot, anchors: a };
  }
  // Phase 2: Growing rotation + every-5-min status ping handled in UI
  if (nowMs < a.growingEnd) {
    const slot = pickFromRotation(ROT_GROWING, nowMs - a.arrivalEnd);
    return { phase: PHASES.GROWING, scene: slot, anchors: a };
  }
  // Phase 3: Buildup
  if (nowMs < a.buildupEnd) {
    return { phase: PHASES.BUILDUP, scene: { id: "buildup" }, anchors: a };
  }
  // Phase 4: Countdown to cake
  if (nowMs < a.cake) {
    const minsLeft = Math.max(1, Math.ceil((a.cake - nowMs) / 60000));
    return { phase: PHASES.COUNTDOWN, scene: { id: "countdown", minsLeft }, anchors: a };
  }
  // Phase 5: Reveal cinematic — runs for TOTAL_REVEAL_SECS after cake
  const revealEndMs = a.cake + TOTAL_REVEAL_SECS * 1000;
  if (nowMs < revealEndMs) {
    const sceneSlot = pickFromRotation(REVEAL_SCENES, nowMs - a.cake);
    return { phase: PHASES.REVEAL, scene: { id: "reveal-" + sceneSlot.id }, anchors: a };
  }
  // Phase 5b: Cake-cutting visuals → until dinner
  if (nowMs < a.dinner) {
    return { phase: PHASES.CAKE, scene: { id: "cake" }, anchors: a };
  }
  // Phase 6: Dinner rotation → until 30 min before end
  const farewellStart = a.end - 30 * 60 * 1000;
  if (nowMs < farewellStart) {
    const slot = pickFromRotation(ROT_DINNER, nowMs - a.dinner);
    return { phase: PHASES.DINNER, scene: slot, anchors: a };
  }
  // Phase 7: Farewell
  return { phase: PHASES.FAREWELL, scene: { id: "farewell" }, anchors: a };
}

function pickFromRotation(rotation, elapsedMs) {
  const totalSecs = rotation.reduce((a, b) => a + b.secs, 0);
  const cycleSec = Math.floor(elapsedMs / 1000) % totalSecs;
  let acc = 0;
  for (const slot of rotation) {
    acc += slot.secs;
    if (cycleSec < acc) return slot;
  }
  return rotation[0];
}

/**
 * Fast-forward helper: compress (start→end) window into a few minutes for preview.
 * Returns the effective "now" in ms.
 */
export function effectiveNow(cfg, opts) {
  const realNow = Date.now();
  if (!opts || !opts.fastForward || !cfg) return realNow;
  // We collapse the entire (start → end) duration of the real event into
  // `compressMinutes` minutes starting from `previewStart` (which we treat
  // as the visit timestamp). Caller passes previewStart so it's stable across renders.
  const start = ms(cfg.start);
  const end   = ms(cfg.end);
  const totalWindow = end - start;
  const compressMs = (opts.compressMinutes || 5) * 60 * 1000;
  const elapsed = realNow - opts.previewStart;
  const scaled = Math.min(elapsed * (totalWindow / compressMs), totalWindow + 60 * 1000);
  return start + scaled;
}

/**
 * Format ISO into "YYYY-MM-DDTHH:mm" for datetime-local input (in the user's local TZ).
 */
export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes())
  );
}

export function localInputToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

// Telugu month names (1-indexed empty slot 0)
const TE_MONTHS = [
  "జనవరి", "ఫిబ్రవరి", "మార్చి", "ఏప్రిల్", "మే", "జూన్",
  "జులై", "ఆగస్టు", "సెప్టెంబర్", "అక్టోబర్", "నవంబర్", "డిసెంబర్",
];

export function formatEventDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  if (lang === "te") return `${d.getDate()} ${TE_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export function formatEventTime(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const h12 = h % 12 || 12;
  if (lang === "te") {
    let period = "మధ్యాహ్నం";
    if (h < 12) period = "ఉదయం";
    else if (h < 16) period = "మధ్యాహ్నం";
    else if (h < 19) period = "సాయంత్రం";
    else period = "రాత్రి";
    return `${period} ${h12}:${m}`;
  }
  return `${h12}:${m} ${h >= 12 ? "PM" : "AM"} IST`;
}
